import {
  ColumnConfig,
  FetchParams,
  KapexTable,
  QueryResponse,
} from "@kapex/tables";
import { ScrollArea, useMantineTheme } from "@mantine/core";
import { useContext } from "react";
import { AppContext } from "src/api/AppContext";
import { useTogglableTheme } from "src/utils/theme";

export interface ScrollableDataGridProps<T> {
  columnConfigs: ColumnConfig<T>[];
  dataFetch: (fetchParams: FetchParams) => Promise<QueryResponse<T>>;
  topMargin?: number;
}

const ScrollableDataGrid = <T extends unknown & { id: string }>({
  topMargin,
  ...props
}: ScrollableDataGridProps<T>) => {
  const { isDarkMode } = useContext(AppContext);
  const theme = useMantineTheme();
  const toggleTheme = useTogglableTheme(isDarkMode, theme);

  return (
    <ScrollArea.Autosize
      maxHeight={`calc((100vh - (var(--mantine-header-height, 0px) + var(--mantine-footer-height, 0px) + 32px)) - ${
        topMargin || 0
      }px )`}
      style={{
        minHeight: 300,
        //marginBottom: 8,
        color: theme.colors.secondaryTextColor[2],
      }}
      type="auto"
      scrollbarSize={9}
      sx={(theme) => ({
        ".mantine-ScrollArea-root": {
          borderRadius: 4,
          paddingRight: 1,
        },
        ".mantine-ScrollArea-viewport": {
          borderRight: `1px solid ${theme.colors.secondary[6]}`,
        },
        ".mantine-ScrollArea-scrollbar": {
          background: theme.colors.secondary[6],
          ".mantine-ScrollArea-thumb": {
            backgroundColor: theme.colors.primary[6],
          },
        },
      })}
    >
      <KapexTable<T>
        tableSx={{
          thead: {
            th: {
              ">div": {
                color: theme.colors.secondaryTextColor[6],
              },
            },
            "::after": {
              left: "-1px",
            },
            tr: {
              th: {
                borderBottom: "none !important",
              },
            },
          },
          tbody: {
            tr: {
              td: {
                borderColor: toggleTheme.card_disabled_bg,
                ">div": {
                  color: theme.colors.secondaryTextColor[2],
                },
              },
            },
            "tr:hover": {
              backgroundColor: `${theme.colors.primary[6]} !important`,
              td: {
                ">div": {
                  color: theme.colors.primaryTextColor[6],
                },
              },
            },
          },
          tfoot: {
            td: {
              borderRight: 0,
              div: {
                color: theme.colors.secondaryTextColor[6],
              },
              ">div>div>button": {
                backgroundColor: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                  color: theme.colors.primaryTextColor[4],
                },
              },
            },
          },
        }}
        headerBg={theme.colors.secondary[6]}
        borderColor={theme.colors.secondary[6]}
        {...props}
      />
    </ScrollArea.Autosize>
  );
};

export default ScrollableDataGrid;
